import React, { useState, useEffect } from 'react';
import { dateTimeToSGTIso } from '../utils/dateUtils';
import '../assets/RecurringPaymentForm.css';

const RecurringPaymentForm = ({ 
  tutors, 
  onSubmit, 
  onCancel, 
  initialData = null,
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    tutor: '',
    amount: '',
    type: 'admin_stipend',
    reason: '',
    startDate: '',
    endDate: ''
  });

  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showTutorDropdown, setShowTutorDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Populate form with initial data if editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        tutor: initialData.tutor?._id || initialData.tutor,
        amount: initialData.amount.toString(),
        type: initialData.type,
        reason: initialData.reason,
        startDate: initialData.startDate ? formatDateForInput(initialData.startDate) : '',
        endDate: initialData.endDate ? formatDateForInput(initialData.endDate) : ''
      });
      
      // Set search term to tutor name if available
      if (initialData.tutor?.name) {
        setSearchTerm(initialData.tutor.name);
      }
    }
  }, [initialData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.tutor-selector')) {
        setShowTutorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    // Show the SGT calendar date of the stored UTC value (matches todaySGT()).
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' });
  };

  const paymentTypes = [
    { value: 'admin_stipend', label: 'Admin Stipend' },
    { value: 'performance_bonus', label: 'Performance Bonus' },
    { value: 'custom', label: 'Custom' }
  ];

  const getFilteredTutors = () => {
    if (!searchTerm) return tutors;
    
    return tutors.filter(t => 
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleTutorSelect = (tutor) => {
    setFormData({ ...formData, tutor: tutor._id });
    setSearchTerm(tutor.name);
    setShowTutorDropdown(false);
    setErrors({ ...errors, tutor: '' });
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tutor) {
      newErrors.tutor = 'Please select a tutor';
    }

    if (!formData.amount || formData.amount === '0') {
      newErrors.amount = 'Amount must be greater than zero';
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      newErrors.amount = 'Please enter a valid positive number';
    }

    if (!formData.reason || formData.reason.trim().length === 0) {
      newErrors.reason = 'Reason is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    // Validate end date if provided
    if (formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        tutor: formData.tutor,
        amount: parseFloat(formData.amount),
        type: formData.type,
        reason: formData.reason.trim(),
        // Interpret the picked calendar date as SGT and send a UTC ISO string
        // (matches attendance submission). Backend create does new Date(startDate).
        startDate: dateTimeToSGTIso(formData.startDate),
        // Always send endDate so the backend can clear it when editing.
        // New records with no end date send null (backend treats it as indefinite).
        endDate: formData.endDate ? dateTimeToSGTIso(formData.endDate) : null
      };

      await onSubmit(submitData);

      // Reset form if not editing
      if (!isEdit) {
        setFormData({
          tutor: '',
          amount: '',
          type: 'admin_stipend',
          reason: '',
          startDate: '',
          endDate: ''
        });
        setSearchTerm('');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Surface the server's message in a popup (e.g. blocked because the edit
      // would change an already-paid month) so the admin sees why it failed.
      const serverMessage = error?.response?.data?.message;
      if (serverMessage) {
        window.alert(serverMessage);
      } else {
        window.alert('Failed to save recurring payment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="recurring-payment-form">
      <h3>{isEdit ? 'Edit Recurring Payment' : 'Add Recurring Payment'}</h3>
      
      <form onSubmit={handleSubmit}>
        {/* Tutor Selector */}
        <div className="form-group">
          <label>Tutor *</label>
          <div className="tutor-selector">
            <input
              type="text"
              className={`tutor-search ${errors.tutor ? 'error' : ''}`}
              placeholder="Search tutor..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowTutorDropdown(true);
              }}
              onFocus={() => setShowTutorDropdown(true)}
              disabled={isEdit} // Don't allow changing tutor when editing
            />
            {showTutorDropdown && !isEdit && (
              <div className="tutor-dropdown">
                {getFilteredTutors().length === 0 ? (
                  <div className="no-results">No tutors found</div>
                ) : (
                  getFilteredTutors().map(tutor => (
                    <div
                      key={tutor._id}
                      className="tutor-option"
                      onClick={() => handleTutorSelect(tutor)}
                    >
                      <div className="tutor-name">{tutor.name}</div>
                      {tutor.email && (
                        <div className="tutor-email">{tutor.email}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {errors.tutor && <span className="error-message">{errors.tutor}</span>}
        </div>

        {/* Amount */}
        <div className="form-group">
          <label>Amount ($) *</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            placeholder="Enter amount"
            value={formData.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            className={errors.amount ? 'error' : ''}
          />
          <small className="help-text">
            Enter the monthly recurring payment amount
          </small>
          {errors.amount && <span className="error-message">{errors.amount}</span>}
        </div>

        {/* Type */}
        <div className="form-group">
          <label>Type *</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
          >
            {paymentTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Reason */}
        <div className="form-group">
          <label>Reason *</label>
          <textarea
            placeholder="Enter reason for recurring payment"
            value={formData.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            rows={4}
            className={errors.reason ? 'error' : ''}
          />
          <small className="help-text">
            {formData.reason.length}/10 characters minimum
          </small>
          {errors.reason && <span className="error-message">{errors.reason}</span>}
        </div>

        {/* Start Date */}
        <div className="form-group">
          <label>Start Date *</label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
            className={errors.startDate ? 'error' : ''}
          />
          <small className="help-text">
            The date when this recurring payment should start being applied
          </small>
          {errors.startDate && <span className="error-message">{errors.startDate}</span>}
        </div>

        {/* End Date (Optional) */}
        <div className="form-group">
          <label>End Date (Optional)</label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => handleChange('endDate', e.target.value)}
            className={errors.endDate ? 'error' : ''}
            min={formData.startDate}
          />
          <small className="help-text">
            Leave blank for indefinite recurring payment. The payment will automatically stop after this date.
          </small>
          {errors.endDate && <span className="error-message">{errors.endDate}</span>}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancel" 
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-submit"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (isEdit ? 'Update Recurring Payment' : 'Add Recurring Payment')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RecurringPaymentForm;
